import { AxiosError } from 'axios';

import { axiosInstanceForTenantAPI } from '../../assets/utils/axiosInstance';
import {
  AdditionalInformationOriginalProps,
  AuditLogsFromReportDetailsOriginalProps,
  InternalCommentFromReportDetailsOriginalProps,
  MessagesFromReportDetailsOriginalProps,
  ReportDetailsProp,
  ResponseFiles,
  TenantNamesProps,
} from '../../components/customer/report/types';
import {
  AttachmentList,
  AttachmentObject,
} from '../../interfaces/AttachmentObject';

const transformFiles = (responseFiles: ResponseFiles[]): AttachmentList[] => {
  const transformedFiles = responseFiles.map(
    ({ files, uploader_id, uploader_name }) => {
      const fileList: AttachmentObject[] = files.map((file) => ({
        id: file.id,
        name: file.original_file_name,
        fileType: file.file_type,
        url: file.url,
        attachmentType: file.attachment_type,
        userType: file.user_type,
      }));
      return {
        files: fileList,
        uploaderID: uploader_id,
        uploaderName: uploader_name,
      };
    },
  );
  return transformedFiles;
};

const transformFilesFromReportInfo = (reportInfo: ResponseFiles[]) => {
  let attachments: Array<AttachmentList> = [];
  attachments = [...attachments, ...transformFiles(reportInfo)];
  return attachments;
};

const transformAuditLogs = (
  logs: Array<AuditLogsFromReportDetailsOriginalProps>,
) => {
  return logs.map(
    ({
      author,
      activity,
      created_at,
    }: AuditLogsFromReportDetailsOriginalProps) => {
      return {
        name: author?.full_name || '',
        activity,
        createdAt: created_at,
      };
    },
  );
};

const transformMessages = (
  files: Array<MessagesFromReportDetailsOriginalProps>,
) => {
  return files.map(
    ({
      content,
      from_user_full_name,
      created_at,
      messageId,
    }: MessagesFromReportDetailsOriginalProps) => {
      return {
        messageId,
        content,
        name: from_user_full_name,
        createdAt: created_at,
      };
    },
  );
};

const transformInternalComments = (
  logs: Array<InternalCommentFromReportDetailsOriginalProps>,
) => {
  return logs.map(
    ({
      id,
      author,
      content,
      created_at,
      category,
    }: InternalCommentFromReportDetailsOriginalProps) => {
      return {
        id: id,
        name: author?.full_name || '',
        content,
        createdAt: created_at,
        category: category ?? null,
      };
    },
  );
};

const transformAdditionalInformation = (
  data: Array<AdditionalInformationOriginalProps>,
): Array<{ descriptionText: string }> => {
  return data?.map(({ description_text }) => ({
    descriptionText: description_text,
  }));
};

export const caseManagerReportFetcher = async (url: string) => {
  const response = await axiosInstanceForTenantAPI()
    .get(url)
    .catch((err: Error | AxiosError) => {
      throw err;
    });

  const {
    description_pii_removed_ranges: reportPiiRemovedDescriptionRanges,
    description_redacted_ranges: reportRedactedDescriptionRanges,
    country_verbose: country,
    followers,
    priority,
    assignee,
    status,
    tags,
    department,
    conversation,
    description,
    id: reportID,
    reference_id: referenceId,
    report_type: reportType,
    created_at: createdAt,
    due_date: firstDueDate,
    final_due_date: finalDueDate,
    linked_reports: linkedReports,
    report_messages: reportMessages,
    audit_logs: auditLogs,
    additional_information: additionalInformation,
    report_informations: reportInformation,
    internal_comments: internalComments,
    user_tenants: allUserTenant,
    user_full_name: userFullName,
    tenant_reports: allTenantReport,
    report_custom_fields: customFields,
    is_case_locked_enabled: isCaseLockedEnabled,
    completed_date: completedAt,
    is_internally_submitted: isInternalSubmission,
    subject_individual: subjectIndividual,
    suspected_department: suspectedDepartment,
    is_whistleblower_feedback_enabled: isWhistleblowerFeedbackEnabled,
    is_assignee: isAssignee,
    description_detected_language_ids: descriptionDetectedLanguage,
  } = response.data;

  const processedData: ReportDetailsProp = {
    reportPiiRemovedDescriptionRanges,
    reportRedactedDescriptionRanges,
    country,
    reportId: reportID,
    referenceId,
    priority,
    firstDueDate,
    finalDueDate,
    status,
    tags,
    department,
    subjectIndividual,
    suspectedDepartment,
    conversation,
    linkedReports,
    reportType,
    additionalInformation: Array.isArray(additionalInformation)
      ? transformAdditionalInformation(additionalInformation)
      : [],
    reportInformation,
    allUserTenant,
    allTenantReport,
    createdAt,
    completedAt,
    description,
    reporter: userFullName,
    assignee: assignee ? assignee.id : '',
    assigneeName: assignee ? assignee.full_name : '',
    followers: followers.map((user: TenantNamesProps) => user.id),
    reportAttachments: Array.isArray(reportInformation)
      ? transformFilesFromReportInfo(reportInformation)
      : [],
    reportMessages: Array.isArray(reportMessages)
      ? transformMessages(reportMessages)
      : [],
    auditLogs: Array.isArray(auditLogs) ? transformAuditLogs(auditLogs) : [],
    internalComments: Array.isArray(internalComments)
      ? transformInternalComments(internalComments)
      : [],
    isCaseLockedEnabled,
    customFields,
    isInternalSubmission,
    isWhistleblowerFeedbackEnabled,
    isAssignee,
    descriptionDetectedLanguage,
  };

  return processedData;
};
